import type { Material, Object3D, Texture } from 'three';
import { EquirectangularReflectionMapping, Mesh, MeshStandardMaterial, PlaneGeometry } from 'three';

import { applySurroundMaterials, disposeUnusedSurroundMaterials } from '../../managers/cutout-manager/helpers';
import { createPlainMaterial, createSurroundMaterialSet } from '../../builders/geometry-builder/helpers/plain-material.helpers';
import { DESIGN_PACKAGE_ENV_SKY_KEY, DESIGN_PACKAGE_LOOK } from '../../../constants/design-package-look';
import { getEffectiveTextureQuality, getSkyResolutionForQuality, type QualitySettings } from '../../../hooks/use-three-kitchen/quality';
import { SKY_TEXTURES, type SkyResolutionVariants } from '../../../constants/texture-options';
import type { AnimationManager } from '../../../three-helpers/animation';
import {
	createCountertopTextureInfo,
	createFloorTextureInfo,
	createSkyTextureInfo,
	createSurroundTextureInfo,
	createTrimTextureInfo,
	KitchenSceneBuilder
} from '../../builders';
import type { ISceneBuilder } from '../../builders';
import { normalizeCountertopKey } from '../../builders/kitchen-scene-builder/helpers';
import type {
	ExportManager,
	LightingManager,
	ModelManager,
	SceneManager,
	TextureManager
} from '../../managers';
import type { EngineConfig } from '../../types';
import { analyzeImageBrightness } from '../../managers/lighting-manager/helpers/analyze-image-brightness';
import { upgradeSkyTextureCfn } from './functions/upgrade-sky-texture.cfn';

export type SceneTextureDelegateConfig = {
	textureManager: TextureManager | null;
	sceneManager: SceneManager | null;
	modelManager: ModelManager | null;
	exportManager: ExportManager | null;
	animationManager: AnimationManager | null;
	lightingManager: LightingManager | null;
	config: EngineConfig;
	qualitySettings: QualitySettings;
	builders: Map<string, ISceneBuilder>;
	requestRender: () => void;
};

/**
 * Delegate that owns texture/material change operations and sky/floor foundation.
 * Extracted from ThreeSceneEngine to reduce file size.
 */
export class SceneTextureDelegate {
	#textureManager: TextureManager | null;
	#sceneManager: SceneManager | null;
	#modelManager: ModelManager | null;
	#exportManager: ExportManager | null;
	#animationManager: AnimationManager | null;
	#lightingManager: LightingManager | null;
	#config: EngineConfig;
	#qualitySettings: QualitySettings;
	#builders: Map<string, ISceneBuilder>;
	#requestRender: () => void;

	// Texture state
	#currentSurroundKey: string | null = null;
	#currentSkyKey: string | null = null;
	#isSkyUpgrading = false;
	#reflectionEnvMap: Texture | null = null;

	// Texture quality override. null = follow config.quality (renderQuality).
	#textureQuality: number | null = null;

	// Tracks whether the scene is currently using a custom (user-uploaded) sky
	// so that brightness adjustments can be restored when switching back to presets
	#isCustomSky = false;

	// Material keys that could not be applied (material missing after on-demand load attempt)
	#materialApplicationFailures: Set<string> = new Set();

	static readonly REFLECTION_SKY_KEY = 'sky/rainforest_trail';

	constructor (config: SceneTextureDelegateConfig) {
		this.#textureManager = config.textureManager;
		this.#sceneManager = config.sceneManager;
		this.#modelManager = config.modelManager;
		this.#exportManager = config.exportManager;
		this.#animationManager = config.animationManager;
		this.#lightingManager = config.lightingManager;
		this.#config = config.config;
		this.#qualitySettings = config.qualitySettings;
		this.#builders = config.builders;
		this.#requestRender = config.requestRender;
		this.#textureQuality = config.config.textureQuality ?? null;
	}

	// ========== Public Getters ==========

	get currentSkyKey (): string | null {
		return this.#currentSkyKey;
	}

	set currentSkyKey (key: string | null) {
		this.#currentSkyKey = key;
	}

	get currentSurroundKey (): string | null {
		return this.#currentSurroundKey;
	}

	set currentSurroundKey (key: string | null) {
		this.#currentSurroundKey = key;
	}

	// ========== Manager Updates ==========

	updateManagers (config: Partial<SceneTextureDelegateConfig>): void {
		if (config.textureManager !== undefined) this.#textureManager = config.textureManager;
		if (config.sceneManager !== undefined) this.#sceneManager = config.sceneManager;
		if (config.modelManager !== undefined) this.#modelManager = config.modelManager;
		if (config.exportManager !== undefined) this.#exportManager = config.exportManager;
		if (config.animationManager !== undefined) this.#animationManager = config.animationManager;
		if (config.lightingManager !== undefined) this.#lightingManager = config.lightingManager;
		if (config.requestRender !== undefined) this.#requestRender = config.requestRender;
	}

	// ========== Scene Foundation ==========

	/**
	 * Load sky and floor textures as the scene's foundation layer.
	 * Camera positioning is handled by the caller (engine).
	 */
	async loadSceneFoundation (options: {
		skyKey?: string;
		floorKey?: string;
		quality?: number;
	} = {}, isDisposed: boolean): Promise<void> {
		if (!this.#textureManager || !this.#sceneManager) return;

		const quality = options.quality ?? Math.abs(this.#config.quality ?? 7);
		const textureInfos: Array<import('../../../model/texture-info').TextureInfo> = [];

		if (options.skyKey && options.skyKey !== 'background') {
			const skyInfo = createSkyTextureInfo(options.skyKey, quality);
			if (skyInfo) textureInfos.push(skyInfo);

			const reflectionSkyKey = SceneTextureDelegate.REFLECTION_SKY_KEY;
			if (options.skyKey !== reflectionSkyKey) {
				const reflectionSkyInfo = createSkyTextureInfo(reflectionSkyKey, quality);
				if (reflectionSkyInfo) textureInfos.push(reflectionSkyInfo);
			}
		}

		if (options.floorKey) {
			const floorInfo = createFloorTextureInfo(options.floorKey);
			if (floorInfo) textureInfos.push(floorInfo);
		}

		if (textureInfos.length === 0) return;

		await this.#textureManager.loadTextures(textureInfos);

		if (isDisposed || !this.#sceneManager) return;

		if (options.skyKey && options.skyKey !== 'background') {
			this.#currentSkyKey = options.skyKey;
			const { scene } = this.#sceneManager;
			if (scene) {
				// Use the raw equirectangular texture for the background — no
				// PMREM needed.  Three.js renders equirectangular backgrounds
				// natively.  PMREM for reflections is deferred to idle time.
				const rawTexture = this.#textureManager.textures.get(options.skyKey);
				if (rawTexture) {
					rawTexture.mapping = EquirectangularReflectionMapping;
					scene.background = rawTexture;
				}

				// Defer PMREM for environment reflections to idle time
				this.deferEnvironmentMapSetup(options.skyKey);
			}
		}

		if (options.floorKey) {
			const floorMaterial = this.#textureManager.getClonedMaterial(options.floorKey);
			if (floorMaterial) {
				const geometry = new PlaneGeometry(1000, 1000, 1, 1);
				const floor = new Mesh(geometry, floorMaterial as MeshStandardMaterial);
				floor.rotation.x = -Math.PI / 2;
				floor.position.y = -0.01;
				floor.castShadow = false;
				floor.receiveShadow = this.#qualitySettings?.shadowMapEnabled ?? true;
				floor.userData = {
					id: 'floor',
					type: 'floor',
					isFoundation: true
				};

				const { objectHolder } = this.#sceneManager;
				if (objectHolder) {
					objectHolder.add(floor);
				}
			}
		}
	}

	/**
	 * Remove the foundation floor mesh (tagged with isFoundation).
	 */
	removeFoundationFloor (): void {
		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder) return;

		for (const child of [...objectHolder.children]) {
			if (child.userData?.type === 'floor' && child.userData?.isFoundation) {
				objectHolder.remove(child);
				if (child instanceof Mesh) {
					child.geometry?.dispose();
					const mat = child.material;
					if (mat && 'dispose' in mat) {
						(mat as Material).dispose();
					}
				}
				break;
			}
		}
	}

	// ========== Environment Map ==========

	/**
	 * Defer PMREM environment map generation to when the browser is idle.
	 * The raw equirectangular texture is used for `scene.background`
	 * immediately (non-blocking).  This method schedules the heavier PMREM
	 * generation for `scene.environment` (PBR reflections) so it never
	 * blocks user interaction.
	 */
	private deferEnvironmentMapSetup (_skyKey: string): void {
		// No-op: PMREM is now generated synchronously during compose().
		// The old requestIdleCallback approach caused freeze-after-first-drag
		// because fromEquirectangular() blocks the main thread ~200ms and
		// fires after the render loop has self-terminated.
	}

	#envMapPending = false;

	/**
	 * Schedule PMREM environment map generation for idle time.
	 * The PMREM is needed for PBR reflections (`scene.environment`)
	 * but is too heavy to run synchronously during init/build.
	 * Materials render without reflections until the idle callback fires.
	 */
	ensureEnvironmentMapForReflections (): void {
		const scene = this.#sceneManager?.scene;
		if (!scene || scene.environment || !this.#textureManager) return;

		const envKeys = [
			...(DESIGN_PACKAGE_LOOK ? [DESIGN_PACKAGE_ENV_SKY_KEY] : []),
			SceneTextureDelegate.REFLECTION_SKY_KEY,
			this.#currentSkyKey,
			'sky/studio_garden',
			'sky/overcast'
		].filter(Boolean) as Array<string>;

		for (const key of envKeys) {
			const setResult = this.#textureManager.setEnvironmentTexture(key);
			if (setResult) {
				const envMap = this.#textureManager.environmentMap;
				if (envMap) {
					scene.environment = envMap;
					if (DESIGN_PACKAGE_LOOK) scene.environmentIntensity = 1;

					if (key === SceneTextureDelegate.REFLECTION_SKY_KEY) {
						this.#reflectionEnvMap = envMap;
					}

					if (this.#modelManager) {
						this.#modelManager.setEnvironmentMap(envMap);
					}
					this.#requestRender();
					break;
				}
			}
		}
	}

	setSceneEnvironment (textureKey: string): void {
		if (!this.#textureManager) return;

		if (!this.#textureManager.setEnvironmentTexture(textureKey)) {
			console.warn('SceneTextureDelegate.setSceneEnvironment: Texture not found:', textureKey);
			return;
		}

		const scene = this.#sceneManager?.scene;
		const envMap = this.#textureManager.environmentMap;

		if (scene && envMap) {
			scene.environment = envMap;

			if (this.#modelManager) {
				this.#modelManager.setEnvironmentMap(envMap);
			}
		}
	}

	// ========== On-Demand Texture Loading ==========

	/**
	 * Load a texture on demand via TextureManager if it is not already cached.
	 * Returns the cloned material once loaded, or null if loading fails.
	 * After loading, calls requestRender() so the scene reflects the new texture.
	 */
	private async loadMaterialOnDemand (textureKey: string): Promise<MeshStandardMaterial | null> {
		if (!this.#textureManager || !this.#config.basePath) return null;

		// Build a TextureInfo for the requested key by consulting all known texture factories.
		// The factories are the single source of truth for texture definitions.
		const textureInfo =
			createSurroundTextureInfo(textureKey) ??
			createCountertopTextureInfo(textureKey) ??
			createFloorTextureInfo(textureKey) ??
			createTrimTextureInfo(textureKey);

		if (!textureInfo) {
			console.warn('SceneTextureDelegate.loadMaterialOnDemand: No TextureInfo definition found for key:', textureKey);
			return null;
		}

		return new Promise<MeshStandardMaterial | null>((resolve) => {
			this.#textureManager!.loadTextures([textureInfo], {
				onComplete: () => {
					const material = this.#textureManager!.getClonedMaterial(textureKey);
					resolve(material as MeshStandardMaterial | null);
				},
				onError: (url: string) => {
					console.warn('SceneTextureDelegate.loadMaterialOnDemand: Failed to load texture:', textureKey, url);
					resolve(null);
				}
			});
		});
	}

	/**
	 * Load a sky texture on demand if it is not already cached.
	 * Returns the raw Texture once loaded, or null if loading fails.
	 */
	private async loadSkyTextureOnDemand (textureKey: string): Promise<Texture | null> {
		if (!this.#textureManager || !this.#config.basePath) return null;

		const renderQuality = Math.abs(this.#config.quality ?? 7);
		const textureInfo = createSkyTextureInfo(textureKey, renderQuality);

		if (!textureInfo) {
			console.warn('SceneTextureDelegate.loadSkyTextureOnDemand: No TextureInfo definition found for key:', textureKey);
			return null;
		}

		return new Promise<Texture | null>((resolve) => {
			this.#textureManager!.loadTextures([textureInfo], {
				onComplete: () => {
					const texture = this.#textureManager!.getTexture(textureKey) ?? null;
					resolve(texture ?? null);
				},
				onError: (url: string) => {
					console.warn('SceneTextureDelegate.loadSkyTextureOnDemand: Failed to load sky texture:', textureKey, url);
					resolve(null);
				}
			});
		});
	}

	// ========== Texture Changes ==========

	private isSlateTexture (textureKey: string): boolean {
		return textureKey.startsWith('slat') ||
			textureKey === 'slatwalnut' ||
			textureKey === 'slatcharcoal' ||
			textureKey === 'slatbright';
	}

	/**
	 * Apply a loaded surround material to all panel and corner meshes in the scene.
	 * Extracted so it can be called both from the synchronous fast-path and the
	 * async on-demand load path.
	 */
	private applySurroundMaterial (material: MeshStandardMaterial, cornerMaterial: MeshStandardMaterial | null): void {
		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder) return;

		objectHolder.traverse((child: Object3D) => {
			if ((child as any).type !== 'Mesh') return;

			const { type: meshType } = child.userData ?? {};

			if (meshType === 'panel') {
				const oldMaterial = (child as any).material;
				const clonedMat = material.clone() as MeshStandardMaterial;
				clonedMat.needsUpdate = true;
				// A cap-split panel keeps its per-face groups through CSG, so re-apply the finish by face.
				// A slat CSG model has no such groups and takes the single material it always took.
				const surroundMaterials = createSurroundMaterialSet(clonedMat);
				if (!applySurroundMaterials(child as Mesh, surroundMaterials)) {
					disposeUnusedSurroundMaterials(surroundMaterials);
					(child as any).material = clonedMat;
				}
				if (oldMaterial) {
					requestAnimationFrame(() => {
						if (Array.isArray(oldMaterial)) {
							oldMaterial.forEach((m: any) => m.dispose());
						} else {
							oldMaterial.dispose();
						}
					});
				}
			} else if (meshType === 'corner' && cornerMaterial) {
				const oldMaterial = (child as any).material;
				(child as any).material = cornerMaterial.clone();
				(child as any).material.needsUpdate = true;
				if (oldMaterial) {
					requestAnimationFrame(() => {
						if (Array.isArray(oldMaterial)) {
							oldMaterial.forEach((m: any) => m.dispose());
						} else {
							oldMaterial.dispose();
						}
					});
				}
			}
		});
	}

	async changeSurroundTexture (textureKey: string): Promise<boolean> {
		const currentIsSlat = this.#currentSurroundKey ? this.isSlateTexture(this.#currentSurroundKey) : false;
		const newIsSlat = this.isSlateTexture(textureKey);

		if (currentIsSlat !== newIsSlat) {
			this.#currentSurroundKey = textureKey;
			// Ensure the surround + corner materials are loaded before signaling a per-island rebuild.
			// Without this, flat-color slats (texture: '') hit the rebuild path before their materials
			// have been registered in the geometry builder's materials map, falling back to a default
			// 0xcccccc material. Materials cached by the textureManager are no-ops here.
			if (!this.#textureManager?.hasMaterial(textureKey)) {
				await this.loadMaterialOnDemand(textureKey);
			}
			const cornerKey = `${textureKey}-corner`;
			if (!this.#textureManager?.hasMaterial(cornerKey)) {
				await this.loadMaterialOnDemand(cornerKey);
			}
			return true;
		}

		this.#currentSurroundKey = textureKey;

		let material = this.#textureManager?.getClonedMaterial(textureKey) as MeshStandardMaterial | null ?? null;

		if (!material) {
			material = await this.loadMaterialOnDemand(textureKey);
		}

		if (!material) {
			console.warn('[changeSurroundTexture] Material not found and could not be loaded on demand:', textureKey);
			return false;
		}

		const cornerKey = `${textureKey}-corner`;
		let cornerMaterial = this.#textureManager?.getClonedMaterial(cornerKey) as MeshStandardMaterial | null ?? null;

		if (!cornerMaterial) {
			// Attempt on-demand load of the corner trim texture
			cornerMaterial = await this.loadMaterialOnDemand(cornerKey);
		}

		if (!this.#sceneManager?.objectHolder) {
			return false;
		}

		this.applySurroundMaterial(material, cornerMaterial);
		this.#requestRender();
		return false;
	}

	async changeCountertopTexture (textureKey: string): Promise<void> {
		const objectHolder = this.#sceneManager?.objectHolder;
		if (!objectHolder) return;

		// Resolve the per-island countertop key for each countertop mesh rather than
		// forcing every countertop to a single key. This keeps per-island "No Countertop"
		// / material overrides intact — otherwise a kitchen-level apply (e.g. the post-build
		// material sync) would clobber an overridden island back to the kitchen default.
		const kitchenBuilder = this.#builders.get('kitchen');
		const islands = kitchenBuilder instanceof KitchenSceneBuilder
			? kitchenBuilder.kitchenDataManagerAccess.getIslands()
			: [];

		// Cache resolved materials per key so shared keys are only loaded once per call.
		const materialCache = new Map<string, MeshStandardMaterial | null>();
		const resolveMaterial = async (key: string): Promise<MeshStandardMaterial | null> => {
			if (materialCache.has(key)) return materialCache.get(key) ?? null;
			let mat = this.#textureManager?.getClonedMaterial(key) as MeshStandardMaterial | null ?? null;
			if (!mat) mat = await this.loadMaterialOnDemand(key);
			materialCache.set(key, mat);
			return mat;
		};

		const countertopMeshes: Object3D[] = [];
		objectHolder.traverse((child: Object3D) => {
			if ((child as any).type === 'Mesh' && child.userData?.type === 'countertop') {
				countertopMeshes.push(child);
			}
		});

		for (const mesh of countertopMeshes) {
			const islandIndex = mesh.userData?.islandIndex as number | undefined;
			const island = typeof islandIndex === 'number' ? islands[islandIndex] : undefined;
			const meshKey = island ? normalizeCountertopKey(island.counter?.material?.key) : textureKey;

			const material = await resolveMaterial(meshKey);
			if (!material) {
				console.warn('SceneTextureDelegate.changeCountertopTexture: Material not found and could not be loaded on demand:', meshKey);
				this.#materialApplicationFailures.add(meshKey);
				continue;
			}

			const oldMaterial = (mesh as any).material;
			(mesh as any).material = [material.clone(), material.clone()];
			(mesh as any).material[0].normalMap = null;
			(mesh as any).material[0].needsUpdate = true;
			(mesh as any).material[1].needsUpdate = true;
			if (oldMaterial) {
				requestAnimationFrame(() => {
					if (Array.isArray(oldMaterial)) {
						oldMaterial.forEach((m: any) => m.dispose());
					} else {
						oldMaterial.dispose();
					}
				});
			}
		}

		this.#requestRender();
	}

	async changeFloorTexture (textureKey: string): Promise<void> {
		const scene = this.#sceneManager?.scene;
		if (!scene) {
			console.warn('SceneTextureDelegate.changeFloorTexture: Scene not available');
			return;
		}

		const isHidden = textureKey === 'floor/hide';

		if (isHidden) {
			scene.traverse((child: Object3D) => {
				if ((child as any).type === 'Mesh' && child.userData?.type === 'floor') {
					child.visible = false;
				}
			});
			this.#requestRender();
			return;
		}

		let material = this.#textureManager?.getClonedMaterial(textureKey) as MeshStandardMaterial | null ?? null;

		if (!material) {
			material = await this.loadMaterialOnDemand(textureKey);
		}

		if (!material) {
			console.warn('SceneTextureDelegate.changeFloorTexture: Material not found and could not be loaded on demand:', textureKey);
			return;
		}

		let floorFound = false;
		scene.traverse((child: Object3D) => {
			if ((child as any).type === 'Mesh' && child.userData?.type === 'floor') {
				floorFound = true;
				child.visible = true;
				const oldMaterial = (child as any).material;
				(child as any).material = material;
				(child as any).material.needsUpdate = true;
				if (oldMaterial) {
					requestAnimationFrame(() => oldMaterial.dispose());
				}
			}
		});

		if (!floorFound) {
			console.warn('SceneTextureDelegate.changeFloorTexture: No floor mesh found in scene');
		}

		this.#requestRender();
	}

	// ========== Sky Texture ==========

	async changeSkyTexture (textureKey: string): Promise<void> {
		if (!this.#textureManager) return;

		let backgroundTexture = this.#textureManager.getTexture(textureKey);

		if (!backgroundTexture) {
			backgroundTexture = await this.loadSkyTextureOnDemand(textureKey) ?? undefined;
			if (!backgroundTexture) return;
		}

		const scene = this.#sceneManager?.scene;
		if (!scene) return;

		// When switching from a custom sky back to a built-in preset,
		// restore the default lighting intensities that were adjusted for the custom image
		if (this.#isCustomSky && this.#lightingManager) {
			this.#lightingManager.restoreDefaultBrightness();
			this.#isCustomSky = false;
		}

		if (this.#currentSkyKey === textureKey && scene.environment) {
			this.triggerSkyUpgradeIfNeeded(textureKey);
			return;
		}

		this.#currentSkyKey = textureKey;

		// Use raw equirectangular texture for background (no PMREM, non-blocking)
		const rawTexture = this.#textureManager.textures.get(textureKey);
		if (rawTexture) {
			rawTexture.mapping = EquirectangularReflectionMapping;
			scene.background = rawTexture;
		}

		// Defer PMREM for reflections to idle time
		this.deferEnvironmentMapSetup(textureKey);

		this.#requestRender();
		this.triggerSkyUpgradeIfNeeded(textureKey);
	}

	/**
	 * Schedule a sky quality upgrade after a short delay so the scene is
	 * interactive before the (still somewhat heavy) HDR parse + PMREM
	 * generation runs.  The upgrade cfn now yields between heavy steps
	 * so the main thread stays responsive during the upgrade.
	 */
	private triggerSkyUpgradeIfNeeded (textureKey: string): void {
		const kitchenBuilder = this.#builders.get('kitchen') as any;
		if (kitchenBuilder?._pendingSkyUpgrades) {
			const pendingUpgrades = kitchenBuilder._pendingSkyUpgrades as Array<{
				key: string;
				targetTexture: string;
			}>;

			const upgradeIndex = pendingUpgrades.findIndex((u) => u.key === textureKey);
			if (upgradeIndex !== -1) {
				const upgrade = pendingUpgrades[upgradeIndex];
				pendingUpgrades.splice(upgradeIndex, 1);

				setTimeout(() => {
					this.upgradeSkyTextureWithFade(upgrade.key, upgrade.targetTexture).catch((error) => {
						console.warn('SceneTextureDelegate: Sky texture upgrade failed:', textureKey, error);
					});
				}, 2000);
				return;
			}
		}

		setTimeout(() => {
			this.updateSkyTextureForQuality(
				getEffectiveTextureQuality(this.#textureQuality, Math.abs(this.#config.quality ?? 7))
			);
		}, 2000);
	}

	/**
	 * Set texture quality tier at runtime without rebuilding the scene.
	 * null = follow render quality. 0 (or 1) = clear sky. Otherwise upgrades sky to the new tier.
	 * Returns a promise that resolves once the sky upgrade (if any) has completed,
	 * so callers can await it to drive a loading indicator.
	 */
	async setTextureQuality (textureQuality: number | null): Promise<void> {
		this.#textureQuality = textureQuality;
		const renderQuality = Math.abs(this.#config.quality ?? 7);
		const effective = getEffectiveTextureQuality(textureQuality, renderQuality);
		// getSkyResolutionForQuality returns [] for <= 1 — treat that as "no sky"
		if (effective <= 1) {
			this.clearSkyTexture();
			return;
		}
		await this.updateSkyTextureForQuality(effective);
	}

	async setCustomSkyTexture (dataUrl: string): Promise<void> {
		if (!this.#textureManager) return;

		const texture = await this.#textureManager.loadCustomSkyTexture(dataUrl);
		const scene = this.#sceneManager?.scene;
		if (scene && texture) {
			scene.background = texture;
			await this.changeFloorTexture('floor/hide');

			if (!scene.environment) {
				this.ensureEnvironmentMapForReflections();
			}

			// Analyze the uploaded image brightness and adjust scene lighting to match.
			// This only runs for custom (user-uploaded) sky textures, never for built-in presets.
			this.#isCustomSky = true;
			if (this.#lightingManager) {
				try {
					const brightness = await analyzeImageBrightness({ dataUrl });
					this.#lightingManager.applyCustomSkyBrightness(brightness);
				} catch (_error) {
					// If analysis fails, leave lighting at defaults
				}
			}
		}
	}

	async upgradeSkyTextureWithFade (
		textureKey: string,
		targetTextureFilename: string,
		fadeDuration = 300
	): Promise<void> {
		if (this.#isSkyUpgrading) return;
		this.#isSkyUpgrading = true;

		if (!this.#textureManager || !this.#animationManager || !this.#sceneManager?.scene) {
			this.#isSkyUpgrading = false;
			return;
		}

		return upgradeSkyTextureCfn({
			textureKey,
			targetTextureFilename,
			fadeDuration,
			textureManager: this.#textureManager,
			animationManager: this.#animationManager,
			scene: this.#sceneManager.scene,
			modelManager: this.#modelManager,
			reflectionSkyKey: SceneTextureDelegate.REFLECTION_SKY_KEY,
			currentSkyKey: this.#currentSkyKey,
			setReflectionEnvMap: (envMap) => { this.#reflectionEnvMap = envMap; },
			setIsSkyUpgrading: (value) => { this.#isSkyUpgrading = value; }
		});
	}

	async processPendingSkyUpgrades (
		pendingUpgrades: Array<{ key: string; targetTexture: string }>,
		currentSkyKey: string
	): Promise<void> {
		if (!pendingUpgrades || pendingUpgrades.length === 0) return;

		const currentUpgrade = pendingUpgrades.find((u) => u.key === currentSkyKey);
		if (!currentUpgrade) return;

		await this.upgradeSkyTextureWithFade(
			currentUpgrade.key,
			currentUpgrade.targetTexture
		);
	}

	clearSkyTexture (): void {
		const scene = this.#sceneManager?.scene;
		if (scene) {
			scene.background = null;
		}

		// Restore default lighting when clearing a custom sky
		if (this.#isCustomSky && this.#lightingManager) {
			this.#lightingManager.restoreDefaultBrightness();
			this.#isCustomSky = false;
		}
	}

	clearFloorTexture (): void {
		const scene = this.#sceneManager?.scene;
		if (!scene) return;

		scene.traverse((child: Object3D) => {
			if ((child as any).type === 'Mesh' && child.userData?.type === 'floor') {
				child.visible = false;
			}
		});
	}

	async updateSkyTextureForQuality (quality: number): Promise<void> {
		if (!this.#currentSkyKey) return;

		const skyOption = SKY_TEXTURES.find((s) => s.key === this.#currentSkyKey);
		if (!skyOption?.textures || !skyOption.texture) return;

		const resolutionChain = getSkyResolutionForQuality(quality);
		if (resolutionChain.length === 0) return;

		let targetTexture = skyOption.texture;
		for (const resolution of resolutionChain) {
			if (resolution === 'default') {
				targetTexture = skyOption.texture;
				break;
			}
			const textureFile = skyOption.textures[resolution as keyof SkyResolutionVariants];
			if (textureFile) {
				targetTexture = textureFile;
				break;
			}
		}

		if (targetTexture === skyOption.texture) return;

		try {
			await this.upgradeSkyTextureWithFade(this.#currentSkyKey, targetTexture);
		} catch (error) {
			console.warn('SceneTextureDelegate: Sky texture quality upgrade failed:', this.#currentSkyKey, error);
		}
	}
}
